import { useState } from "react";
import SectionHeading from "./SectionHeading";

const initialPosts = [
  {
    id: 1,
    user: "屏幕前的观察员",
    meta: "认证 · 追踪整理者",
    hot: 126,
    title: "「限流到底算不算处罚？」——把三次降权公告放在一起看",
    text: "翻了近三个月公告，限流、降权、封禁其实是被当成三种不同工具在用，但很多创作者根本分不清自己被哪一种。建议平台把判定和触发条件写成白话。",
    reply: 88,
    liked: true,
  },
  {
    id: 2,
    user: "审核规则笔记",
    meta: "规则研究员",
    hot: 94,
    title: "整理：同一关键词，为何在不同时间判定不同？",
    text: "把被删/被留的内容按时间做了对照表，发现尺度确实会随专项治理周期变化。这不是阴谋论，是规则节奏问题——但也说明规则不够稳定。",
    reply: 57,
    liked: false,
  },
  {
    id: 3,
    user: "中长视频搬砖人",
    meta: "创作者 · 内容产出方",
    hot: 61,
    title: "申诉了三次都没用，到底找谁？",
    text: "作为小创作者，最怕的不是被罚，而是找不到一个能对话的人。希望平台把「人工复核」的入口做得更明显，别让机器人客服当唯一出口。",
    reply: 203,
    liked: true,
  },
];

const relatedThreads = [
  "短剧下架范围是否会波及普通剧集？",
  "未成年人审核，家长视角怎么看？",
  "流量下滑到底是限流还是内容不行？",
  "平台规则更新，创作者该怎么合规？",
];

export default function HotComments() {
  const [posts, setPosts] = useState(initialPosts);
  const [text, setText] = useState("");
  const [posted, setPosted] = useState(false);

  const addPost = (e: React.FormEvent) => {
    e.preventDefault();
    const val = text.trim();
    if (!val) return;
    setPosts([
      {
        id: Date.now(),
        user: "我",
        meta: "刚刚加入讨论",
        hot: 0,
        title: "我的跟帖",
        text: val,
        reply: 0,
        liked: false,
      },
      ...posts,
    ]);
    setText("");
    setPosted(true);
    window.setTimeout(() => setPosted(false), 2200);
  };

  const toggleLike = (id: number) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, liked: !p.liked, hot: p.liked ? p.hot - 1 : p.hot + 1 } : p
      )
    );
  };

  return (
    <section id="hot" className="w-full bg-background-100">
      <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-[1fr_340px] lg:gap-12">
          {/* 左：讨论帖流 */}
          <div>
            <SectionHeading
              kicker="社区热议"
              title="热议区 · 一起来拆"
              desc="围绕官方处罚、下架与审核尺度，把疑问摊开聊。言论归言论，各抒己见，但请文明交流。"
            />
            <div className="mt-8 flex flex-col gap-4">
              {posts.map((p) => (
                <article key={p.id} className="rounded-xl border border-background-200 bg-background-50 p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {p.user.slice(0, 1)}
                    </span>
                    <div className="flex flex-col">
                      <span className="font-label text-sm font-semibold text-foreground-950">{p.user}</span>
                      <span className="text-[11px] text-foreground-500">{p.meta}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleLike(p.id)}
                      className={`ml-auto inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                        p.liked
                          ? "border-primary-200 bg-primary-50 text-primary-700"
                          : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-200"
                      }`}
                    >
                      <i className={`${p.liked ? "ri-fire-fill" : "ri-fire-line"}`} />
                      {p.hot} 热度
                    </button>
                  </div>

                  <h3 className="mt-3 font-label text-base font-semibold leading-snug text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.text}</p>

                  <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-chat-3-line" /> {p.reply} 条回应
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-eye-line" /> 展开全部回应
                    </span>
                    <span className="ml-auto whitespace-nowrap text-primary-600">进入帖子讨论</span>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* 右：发起讨论 + 相关议题 */}
          <aside className="flex flex-col gap-5">
            <form
              onSubmit={addPost}
              className="flex flex-col gap-3 rounded-xl border border-background-200 bg-background-50 p-4"
            >
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                  我
                </span>
                <div className="flex flex-col">
                  <span className="font-label text-sm font-semibold text-foreground-950">发起一个讨论</span>
                  <span className="text-[11px] text-foreground-500">友善发言，理性吃瓜</span>
                </div>
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={200}
                placeholder="围绕处罚、下架、审核，说说你的看法或补充线索…"
                className="h-24 w-full resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
              />
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-foreground-400">{text.length}/200</span>
                <button
                  type="submit"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  发布讨论
                  <i className="ri-send-plane-line" />
                </button>
              </div>
              {posted && (
                <p className="rounded-md bg-accent-100 px-3 py-2 text-xs text-accent-900">
                  已发到你的本页讨论流（演示环境，仅前端展示）。
                </p>
              )}
            </form>

            <div className="rounded-xl border border-background-200 bg-background-50 p-4">
              <h4 className="flex items-center gap-2 font-label text-sm font-bold text-foreground-950">
                <i className="ri-question-line text-primary-600" />
                相关议题，正在讨论
              </h4>
              <ul className="mt-3 flex flex-col">
                {relatedThreads.map((r) => (
                  <li key={r} className="border-b border-background-100 py-2.5 last:border-0">
                    <a
                      href="#hot"
                      className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground-700 hover:text-primary-700"
                    >
                      <i className="ri-chat-smile-3-line mt-0.5 shrink-0 text-accent-600" />
                      {r}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-3 rounded-xl bg-foreground-950 p-4 text-background-50">
              <h4 className="font-heading text-base font-bold">错过更新，别错过社区</h4>
              <p className="text-[13px] leading-relaxed text-background-50/80">
                下载社区客户端，第一时间收到处罚、下架与监管的最新进展，和数万网友一起围观拆解。
              </p>
              <a
                href="#top"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-400 px-4 py-2.5 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-300"
              >
                <i className="ri-download-2-line" />
                下载社区客户端
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}